import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { DriveImageFile, findOrCreateGalleryFolder, listGalleryImages, uploadImageToDrive } from '../../services/googleDriveImageService';
import { findOrCreateFolder } from '../../services/googleDriveService';

// ---------------------------------------------------------------------------
// resizeImage — copied from ImageUploader so this modal has no dependency on
// that component.  Resizes to 384×384 with aspect-ratio-fit + centred canvas.
// ---------------------------------------------------------------------------

function resizeImage(file: File, width: number, height: number): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const ratio = Math.min(width / img.width, height / img.height);
        const newWidth = Math.round(img.width * ratio);
        const newHeight = Math.round(img.height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas context not available')); return; }

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(
          img,
          Math.floor((width - newWidth) / 2),
          Math.floor((height - newHeight) / 2),
          newWidth,
          newHeight
        );

        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Canvas is empty')); return; }
          resolve(new File([blob], file.name, { type: blob.type || file.type }));
        }, file.type || 'image/png');
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// GalleryThumbnail — fetches a single Drive image and renders it
// ---------------------------------------------------------------------------

interface ThumbnailProps {
  file: DriveImageFile;
  accessToken: string;
  selected: boolean;
  onClick: () => void;
}

const GalleryThumbnail: React.FC<ThumbnailProps> = ({ file, accessToken, selected, onClick }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;

    (async () => {
      try {
        const res = await fetch(
          `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!res.ok) { if (!cancelled) setFailed(true); return; }
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        if (!cancelled) { setObjectUrl(url); setLoading(false); }
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.id, accessToken]);

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        paddingTop: '100%',
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 1,
        overflow: 'hidden',
        border: selected ? '3px solid' : '3px solid transparent',
        borderColor: selected ? 'primary.main' : 'transparent',
        boxSizing: 'border-box',
        '&:hover': { opacity: 0.85 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.200',
        }}
      >
        {loading && <CircularProgress size={24} />}
        {failed && !loading && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 0.5, textAlign: 'center' }}>
            {file.name}
          </Typography>
        )}
        {objectUrl && (
          <Box
            component="img"
            src={objectUrl}
            alt={file.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// DriveImageGalleryModal
// ---------------------------------------------------------------------------

export interface DriveImageGalleryModalProps {
  open: boolean;
  accessToken: string;
  /** Called when the user confirms their selection.  The modal closes after this resolves. */
  onSelect: (driveFileId: string, accessToken: string) => Promise<void>;
  onClose: () => void;
}

export const DriveImageGalleryModal: React.FC<DriveImageGalleryModalProps> = ({
  open,
  accessToken,
  onSelect,
  onClose,
}) => {
  const [images, setImages] = useState<DriveImageFile[]>([]);
  const [galleryFolderId, setGalleryFolderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load gallery when the modal opens.
  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setImages([]);
    setGalleryFolderId(null);

    (async () => {
      try {
        const parentFolderId = await findOrCreateFolder(accessToken);
        const galleryId = await findOrCreateGalleryFolder(accessToken, parentFolderId);
        const files = await listGalleryImages(accessToken, galleryId);
        if (!cancelled) {
          setGalleryFolderId(galleryId);
          setImages(files);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load gallery');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, accessToken]);

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be selected again if needed.
    e.target.value = '';
    if (!file || !galleryFolderId) return;

    setIsUploading(true);
    setError(null);
    try {
      const resized = await resizeImage(file, 384, 384);
      const driveFileId = await uploadImageToDrive(accessToken, galleryFolderId, resized.name, resized);
      const newEntry: DriveImageFile = { id: driveFileId, name: resized.name };
      setImages((prev) => [newEntry, ...prev]);
      setSelectedId(driveFileId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelect = async () => {
    if (!selectedId) return;
    setIsSelecting(true);
    setError(null);
    try {
      await onSelect(selectedId, accessToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign image');
      setIsSelecting(false);
    }
  };

  const busy = isUploading || isSelecting;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Image from Gallery</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 260 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && images.length === 0 && !error && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No images in your gallery yet. Upload one to get started.
            </Typography>
          </Box>
        )}

        {!loading && images.length > 0 && (
          <ImageList cols={3} gap={8} sx={{ mt: 0 }}>
            {images.map((img) => (
              <ImageListItem key={img.id}>
                <GalleryThumbnail
                  file={img}
                  accessToken={accessToken}
                  selected={selectedId === img.id}
                  onClick={() => setSelectedId(img.id)}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileInputChange}
            disabled={busy}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : <AddPhotoAlternateIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy || !galleryFolderId}
          >
            {isUploading ? 'Uploading…' : 'Upload New Image'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSelect}
            disabled={!selectedId || busy}
            startIcon={isSelecting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {isSelecting ? 'Selecting…' : 'Select'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
